"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import GoldPriceCalc from "../others/GoldPriceCalc";
import HeadingSection from "../others/HeadingSection";
import { useIsMobile } from "@/utils/useIsMobile";

gsap.registerPlugin(ScrollTrigger);

type AnimatedCoinProps = {
  frontImage: string;
  backImage: string;
};

const AnimatedCoin: React.FC<AnimatedCoinProps> = ({
  frontImage,
  backImage,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const calculatorRef = useRef<HTMLDivElement>(null);
  const coinShadowRef = useRef<HTMLDivElement>(null);
  const coinTextRef = useRef<HTMLDivElement>(null);
  const hasAnimatedRef = useRef(false);

  const isMob = useIsMobile();

  useEffect(() => {
    const container = containerRef.current!;
    const canvas = canvasRef.current!;
    const calcContainer = calculatorRef.current!;
    const coinShadow = coinShadowRef.current!;
    const goldText = coinTextRef.current!;
    const size = 2;

    // --- Scene Setup ---
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      canvas,
    });

    // Canvas fixed smaller than container (for focus)
    const canvasSize = Math.min(container.clientWidth, 675);
    renderer.setSize(canvasSize, canvasSize);
    // Cap pixel ratio - 3x screens would render 2.25x more pixels for no visible gain
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    // --- Lighting Setup ---
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);

    const keyLight = new THREE.DirectionalLight(0xffffff, 0.5);
    keyLight.position.set(0, 0.9, 3); // front-top
    scene.add(keyLight);

    const fillLight = new THREE.PointLight(0xffffff, 0.6);
    fillLight.position.set(-2, 0, 3); // fill from left
    scene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 1);
    rimLight.position.set(0, 1, -3); // rim highlight from behind
    scene.add(rimLight);

    const frontLight = new THREE.PointLight(0xfff4d1, 1.2, 10);
    frontLight.position.set(0, 0, 10); // glowing front reflection
    scene.add(frontLight);

    // Animate front light for soft glint
    const glint = gsap.to(frontLight, {
      intensity: 1.7,
      duration: 2,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
    });

    // --- Coin Geometry & Materials ---
    const geometry = new THREE.CylinderGeometry(size, size, 0.15, 64);

    const edgeMaterial = new THREE.MeshStandardMaterial({
      color: 0xb5b5b5,
      metalness: 0.2,
      roughness: 0.6,
      envMapIntensity: 1.5,
    });

    const frontMaterial = new THREE.MeshStandardMaterial({
      metalness: 0.8,
      roughness: 0.4,
    });

    const backMaterial = new THREE.MeshStandardMaterial({
      metalness: 0.8,
      roughness: 0.4,
    });

    const loader = new THREE.TextureLoader();
    const textures: THREE.Texture[] = [];

    const loadTexture = (url: string, material: THREE.MeshStandardMaterial) => {
      const apply = (tex: THREE.Texture) => {
        textures.push(tex);
        material.map = tex;
        material.needsUpdate = true;
      };
      loader.load(url, apply, undefined, () => {
        console.warn("WebP not supported, using fallback image.");
        loader.load(url.replace(".webp", ".png"), apply);
      });
    };

    loadTexture(frontImage, frontMaterial);
    loadTexture(backImage, backMaterial);

    const coin = new THREE.Mesh(geometry, [
      edgeMaterial,
      frontMaterial,
      backMaterial,
    ]);
    scene.add(coin);

    // --- Initial Transform ---
    coin.scale.set(0.05, 0.05, 0.05); // tiny but visible from the first frame
    coin.position.y = -3; // starts below view
    coin.rotation.x = Math.PI; // face orientation
    coin.rotation.z = 0;

    gsap.set(calcContainer, { opacity: 0, scale: 0.7 });
    gsap.set([coinShadow, goldText], { opacity: 0 });

    // --- GSAP Animation ---
    // Settle animation: calculator, glow and text play once the coin has landed
    const settleTl = gsap.timeline({ paused: true });
    settleTl.to(
      calcContainer,
      { opacity: 1, scale: 1, duration: 0.6, ease: "power2.out" },
      0,
    );
    settleTl.to(
      [coinShadow, goldText],
      { opacity: 1, duration: 0.7, ease: "none" },
      0.2,
    );

    // Coin intro is driven by scroll: scrolling down moves it forward, scrolling up
    // reverses it. Once it fully lands the trigger is removed, so the coin stays put.
    let scrollTrigger: ScrollTrigger | undefined;

    const coinTl = gsap.timeline({
      paused: true,
      defaults: { ease: "none" }, // scroll position is the easing
      onComplete: () => {
        hasAnimatedRef.current = true;
        scrollTrigger?.kill(false, true); // keep the finished state and timeline
        scrollTrigger = undefined;
        settleTl.play();
      },
    });

    // Rise + grow + flip (1.25 turns, settles face-on)
    coinTl.to(coin.position, { y: 0, duration: 0.8, ease: "power1.out" }, 0);
    // ease-in: stays small while rising, reaches full size right as it lands
    coinTl.to(coin.scale, { x: 1, y: 1, z: 1, duration: 1, ease: "power2.in" }, 0);
    coinTl.to(coin.rotation, { x: Math.PI * 3.5, duration: 1 }, 0);

    if (hasAnimatedRef.current) {
      // Already played (effect re-run) - jump straight to the end state
      coinTl.progress(1, true);
      settleTl.progress(1);
    } else {
      scrollTrigger = ScrollTrigger.create({
        trigger: container,
        start: "top bottom", // section starts entering the viewport
        end: "center 55%", // coin almost at the middle of the screen
        animation: coinTl,
        scrub: 0.6, // seconds of smoothing behind the scrollbar
      });
    }

    // --- Render Loop (only while on screen) ---
    let rafId = 0;
    const animate = () => {
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(animate);
    };
    const startLoop = () => {
      if (!rafId) rafId = requestAnimationFrame(animate);
    };
    const stopLoop = () => {
      cancelAnimationFrame(rafId);
      rafId = 0;
    };

    // --- Intersection Observer (render only while visible) ---
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) startLoop();
      else stopLoop();
    });

    observer.observe(container);

    // --- Handle Resize ---
    const handleResize = () => {
      const canvasSize = Math.min(container.clientWidth, 675);
      renderer.setSize(canvasSize, canvasSize);
      camera.aspect = 1;
      camera.updateProjectionMatrix();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
      stopLoop();
      scrollTrigger?.kill();
      coinTl.kill();
      settleTl.kill();
      glint.kill();
      geometry.dispose();
      [edgeMaterial, frontMaterial, backMaterial].forEach((m) => m.dispose());
      textures.forEach((t) => t.dispose());
      renderer.dispose();
    };
  }, [frontImage, backImage]);

  return (
    <section className="w-full flex items-center justify-center relative pt-20 mb-12 sm:mb-100">
      <div className="absolute text-white top-3 w-full z-1">
        <HeadingSection
          heading="Digital Gold Investment Calculator"
          subHeading="Achieve your financial dreams and get rich by investing in customized gold mutual funds."
        />
      </div>

      <div
        ref={containerRef}
        className="relative w-full h-[800px] flex items-center justify-center overflow-hidden sm:overflow-visible"
      >
        <div
          ref={calculatorRef}
          className="absolute z-2 inset-0 w-82 h-132.5 bg-[#00000096] border-2 border-[#C899004D] m-auto rounded-4xl overflow-hidden text-white opacity-0"
        >
          <GoldPriceCalc />
        </div>
        <canvas
          ref={canvasRef}
          className="absolute sm:relative z-1 w-168.75! h-168.75! object-contain rounded-full"
        />

        <div
          ref={coinShadowRef}
          className="hidden sm:block absolute z-0 inset-0 m-auto w-163.75 h-163.75 object-contain shadow-[0px_0px_130px_50px_#c1a021b5] rounded-full opacity-0"
        />

        <div
          ref={coinTextRef}
          className="hidden sm:block w-full max-w-section absolute -bottom-5 2md:-bottom-10 inset-x-0 mx-auto opacity-0"
        >
          {!isMob && (
            <svg
              viewBox="0 0 800 200"
              xmlns="http://www.w3.org/2000/svg"
              className="opacity-40 "
            >
              <defs>
                <linearGradient
                  id="goldGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="0%"
                >
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="48%" stopColor="#ffed63" />
                  <stop offset="100%" stopColor="#be9200" />
                </linearGradient>
              </defs>
              <text
                x="50%"
                y="50%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontFamily="Inter, sans-serif"
                fontSize="120"
                fontWeight="700"
                fill="transparent"
                stroke="url(#goldGradient)"
                strokeWidth="0.5"
              >
                Gold ka Goal
              </text>
            </svg>
          )}
        </div>
      </div>
    </section>
  );
};

export default AnimatedCoin;
